import {
  applyAnnotationsToSchema,
  generateArraySchema,
  generateBooleanSchema,
  generateContainerSchema,
  generateNumberSchema,
  generateObjectSchema,
  generateRootObjectSchema,
  generateStringSchema,
} from "components/cards/common/schema";
import { BaseAtomSpec, generateAtomContainerSchema } from "components/cards/atoms/BaseAtomSpec";
import { TextParticleSpec } from "components/cards/particles/YinzCam";

// Design-token aliases. The first four resolve to the page-level theme tokens
// (highlight = #004899, foreground = #000020, accent = #1AFFBA, background =
// #FFFFFF). The remaining values are non-tokenised greys lifted from the mock.
const FG = 'var(--theme-mode-foreground-color)';
const BG = 'var(--theme-mode-background-color)';
const HIGHLIGHT = 'var(--theme-mode-highlight-color)';
const ACCENT = 'var(--theme-mode-accent-color)';
const NEUTRAL_BORDER = '#e5e5e5';
const NEUTRAL_BAND = '#e5e5e5';
const TEXT_DARK_GREY = '#4c4c4c';
const TEXT_GREY = '#777';

export const specification = new BaseAtomSpec("Cricket Scorecard Card", generateRootObjectSchema({
  // ── Cricket overrides of base defaults to match the Figma mock ──
  container: generateAtomContainerSchema({
    defaultValues: {
      themeMode: "primary",
      background: { visible: true, color: BG },
      border: { visible: true, width: '1px', style: 'solid', color: NEUTRAL_BORDER, radius: '0' },
    },
  }),

  // ── Section containers (background / border / padding / etc.) ──
  tabContainer: applyAnnotationsToSchema(
    generateContainerSchema("Tab", "Container styling for an unselected tab."),
    { padding: '0.75rem 0', border: { radius: '0.5rem' } }
  ),
  selectedTabContainer: applyAnnotationsToSchema(
    generateContainerSchema("Selected Tab", "Container styling for the selected tab."),
    {
      padding: '0.75rem 0',
      background: { visible: true, color: ACCENT },
      border: { radius: '0.5rem' },
    }
  ),
  sectionHeaderContainer: applyAnnotationsToSchema(
    generateContainerSchema(
      "Section Header Band (Batting)",
      "Container styling for the batting section header band. Bowling sections receive the bowling-specific override below."
    ),
    { background: { visible: true, color: ACCENT } }
  ),
  rowContainer: applyAnnotationsToSchema(
    generateContainerSchema("Player Row", "Container styling for each player row."),
    { border: { visible: true, width: '0 0 1px 0', style: 'solid', color: NEUTRAL_BORDER } }
  ),
  metaPillContainer: applyAnnotationsToSchema(
    generateContainerSchema("Meta Pill", "Container styling for pill-kind meta entries (e.g. handedness pills)."),
    {
      padding: '0.25rem 0.375rem',
      background: { visible: true, color: HIGHLIGHT },
      border: { radius: '0.5rem' },
    }
  ),
  summaryTotalRowContainer: applyAnnotationsToSchema(
    generateContainerSchema(
      "Summary Total Row",
      "Container styling specific to the \"total\" summary row. Cricket has no top divider on Total."
    ),
    {}
  ),

  // ── Atomic spacing fields ──
  paddingRoot: generateStringSchema("Root Padding", "Inner padding of the scorecard root.", {
    defaultValue: '1rem',
  }),
  paddingRootCompact: generateStringSchema(
    "Root Padding (Compact)",
    "Inner padding under the mobile container query (≤ 40rem).",
    { defaultValue: '0.5rem' }
  ),
  gapRoot: generateStringSchema("Root Gap", "Vertical gap between sections at the root.", {
    defaultValue: '2rem',
  }),
  gapRootCompact: generateStringSchema(
    "Root Gap (Compact)",
    "Vertical gap between sections under the mobile container query (≤ 40rem).",
    { defaultValue: '0.375rem' }
  ),
  paddingRow: generateStringSchema("Row Padding", "Padding inside each section header band and player row.", {
    defaultValue: '0.75rem',
  }),
  paddingRowCompact: generateStringSchema(
    "Row Padding (Compact)",
    "Row padding under the mobile container query (≤ 40rem).",
    { defaultValue: '0.25rem 0.375rem' }
  ),
  gapRow: generateStringSchema("Row Gap", "Gap between cells inside each row.", {
    defaultValue: '0',
  }),
  gapRowCompact: generateStringSchema(
    "Row Gap (Compact)",
    "Row cell gap under the mobile container query (≤ 40rem).",
    { defaultValue: '0.25rem' }
  ),
  gapTabsStrip: generateStringSchema("Tabs Strip Gap", "Gap between adjacent tabs in the strip.", {
    defaultValue: '0',
  }),
  gapPlayerCell: generateStringSchema("Player Cell Gap", "Gap between the team logo and player name+meta column.", {
    defaultValue: '0.625rem',
  }),
  gapPlayerMeta: generateStringSchema(
    "Player Meta Gap",
    "Vertical gap between the player name and the meta sub-row.",
    { defaultValue: '0.25rem' }
  ),
  gapMeta: generateStringSchema("Meta Sub-row Gap", "Gap between adjacent items in the meta sub-row.", {
    defaultValue: '0.25rem',
  }),
  gapSummaryRow: generateStringSchema(
    "Summary Row Gap",
    "Gap between the label, detail, and value cells in a summary row.",
    { defaultValue: '0.25rem' }
  ),
  paddingSummaryRow: generateStringSchema(
    "Summary Row Padding",
    "Padding inside each summary row (extras / total).",
    { defaultValue: '0.75rem 1rem' }
  ),

  // ── Typography ──
  txtTabLabel: TextParticleSpec.generateSchema("Tab Label Text", "Typography for unselected tab labels.", {
    defaultValues: {
      size: '1.25rem',
      weight: '700',
      transform: 'uppercase',
      wrap: 'nowrap',
      color: TEXT_DARK_GREY,
    },
    uneditableFields: { text: true },
  }),
  selectedTxtTabLabel: TextParticleSpec.generateSchema(
    "Selected Tab Label Text",
    "Typography for the selected tab label.",
    {
      defaultValues: { size: '1.25rem', weight: '700', transform: 'uppercase', wrap: 'nowrap', color: BG },
      uneditableFields: { text: true },
    }
  ),
  txtSectionHeader: TextParticleSpec.generateSchema(
    "Section Header Text",
    "Typography for the section header band title.",
    {
      defaultValues: { size: '1.25rem', weight: '700', wrap: 'nowrap', color: BG },
      uneditableFields: { text: true },
    }
  ),
  txtColumnHeader: TextParticleSpec.generateSchema(
    "Column Header Text",
    "Typography for column headers.",
    {
      defaultValues: { size: '1rem', weight: '700', align: 'right', color: BG },
      uneditableFields: { text: true },
    }
  ),
  txtPlayerName: TextParticleSpec.generateSchema(
    "Player Name Text",
    "Typography for player names.",
    {
      defaultValues: { size: '1.25rem', weight: '700', wrap: 'nowrap', color: FG },
      uneditableFields: { text: true },
    }
  ),
  txtMetaPill: TextParticleSpec.generateSchema(
    "Meta Pill Text",
    "Typography for pill-kind meta entries.",
    {
      defaultValues: { size: '0.875rem', weight: '500', wrap: 'nowrap', color: BG },
      uneditableFields: { text: true },
    }
  ),
  txtMetaText: TextParticleSpec.generateSchema(
    "Meta Text",
    "Typography for text-kind meta entries (player names within dismissals).",
    {
      defaultValues: { size: '1rem', weight: '500', wrap: 'nowrap', color: TEXT_GREY },
      uneditableFields: { text: true },
    }
  ),
  txtMetaTextSmall: TextParticleSpec.generateSchema(
    "Meta Text (Small)",
    "Typography for small meta connectors (the \"c\"/\"b\" tokens within a dismissal line).",
    {
      defaultValues: { size: '0.875rem', weight: '500', wrap: 'nowrap', color: TEXT_GREY },
      uneditableFields: { text: true },
    }
  ),
  txtStatValue: TextParticleSpec.generateSchema(
    "Stat Value Text",
    "Typography for stat values.",
    {
      defaultValues: { size: '1rem', weight: '500', align: 'right', wrap: 'nowrap', color: TEXT_GREY },
      uneditableFields: { text: true },
    }
  ),
  txtSummaryRow: TextParticleSpec.generateSchema(
    "Summary Row Text (Default)",
    "Catch-all typography for summary-row cells. Used when a more specific cell override is not supplied.",
    {
      defaultValues: { size: '0.875rem', weight: '700', wrap: 'nowrap', color: FG },
      uneditableFields: { text: true },
    }
  ),
  txtSummaryLabel: TextParticleSpec.generateSchema(
    "Summary Row Label",
    "Typography for the label cell on non-total summary rows (e.g. Extras).",
    {
      defaultValues: { size: '1rem', weight: '500', wrap: 'nowrap', color: FG },
      uneditableFields: { text: true },
    }
  ),
  txtSummaryDetail: TextParticleSpec.generateSchema(
    "Summary Row Detail",
    "Typography for the detail cell on summary rows (Extras + Total).",
    {
      defaultValues: { size: '1rem', weight: '500', wrap: 'nowrap', color: TEXT_GREY },
      uneditableFields: { text: true },
    }
  ),
  txtSummaryValue: TextParticleSpec.generateSchema(
    "Summary Row Value",
    "Typography for the value cell on non-total summary rows (e.g. Extras).",
    {
      defaultValues: { size: '1rem', weight: '500', wrap: 'nowrap', color: FG },
      uneditableFields: { text: true },
    }
  ),
  txtSummaryTotalLabel: TextParticleSpec.generateSchema(
    "Summary Total Label",
    "Typography for the label cell on the total summary row.",
    {
      defaultValues: { size: '1.25rem', weight: '700', wrap: 'nowrap', color: FG },
      uneditableFields: { text: true },
    }
  ),
  txtSummaryTotalValue: TextParticleSpec.generateSchema(
    "Summary Total Value",
    "Typography for the value cell on the total summary row.",
    {
      defaultValues: { size: '1.25rem', weight: '700', wrap: 'nowrap', color: FG },
      uneditableFields: { text: true },
    }
  ),

  // ── Cricket-only: innings feed ──
  // The cricket data source still emits `innings[]` — the atom adapts this
  // into the base's tabs/sections model at render time.
  innings: generateArraySchema(
    "Innings",
    "Each inning tab with its own batting/bowling data.",
    generateObjectSchema("Inning", "A single inning.", {
      label: generateStringSchema("Label", "Short inning label, e.g. \"WAR 1ST\"."),
      battingTeamName: generateStringSchema("Batting Team Name", "Name of the batting team."),
      battingColumns: generateArraySchema(
        "Batting Column Headers",
        "Stat column headers for the batting table.",
        generateStringSchema("Header", "A single column header.")
      ),
      battingStats: generateArraySchema(
        "Batting Rows",
        "One row per batter.",
        generateObjectSchema("Batting Row", "A single batter row.", {
          playerName: generateStringSchema("Player Name", "The batter name."),
          handedness: generateStringSchema("Handedness", "e.g. \"RHB\" or \"LHB\"."),
          dismissal: generateStringSchema("Dismissal Detail", "e.g. \"c Pooran b Ugarkar\"."),
          isBold: generateBooleanSchema("Bold", "Render with emphasis (active player)."),
          values: generateArraySchema(
            "Stat Values",
            "Values matching column headers.",
            generateStringSchema("Value", "A stat value.")
          ),
        })
      ),
      extrasLabel: generateStringSchema("Extras Label", "Label for the extras row.", { defaultValue: 'Extras' }),
      extrasDetail: generateStringSchema("Extras Detail", "e.g. \"(b 3, lb 3)\"."),
      extrasValue: generateStringSchema("Extras Value", "Total extras, e.g. \"6\"."),
      totalLabel: generateStringSchema("Total Label", "Label for the total row.", { defaultValue: 'Total' }),
      totalDetail: generateStringSchema("Total Detail", "e.g. \"(54.0 ov, RR: 2.46)\"."),
      totalValue: generateStringSchema("Total Value", "Total runs, e.g. \"133\"."),
      fallOfWicketsText: generateStringSchema("Fall of Wickets", "Full fall-of-wickets string."),
      bowlingTeamName: generateStringSchema("Bowling Team Name", "Name of the bowling team."),
      bowlingColumns: generateArraySchema(
        "Bowling Column Headers",
        "Stat column headers for the bowling table.",
        generateStringSchema("Header", "A single column header.")
      ),
      bowlingStats: generateArraySchema(
        "Bowling Rows",
        "One row per bowler.",
        generateObjectSchema("Bowling Row", "A single bowler row.", {
          playerName: generateStringSchema("Player Name", "The bowler name."),
          values: generateArraySchema(
            "Stat Values",
            "Values matching column headers.",
            generateStringSchema("Value", "A stat value.")
          ),
        })
      ),
    })
  ),
  selectedInningIndex: generateNumberSchema(
    "Selected Inning Index",
    "Zero-based index of the initially selected inning.",
    { defaultValue: 0 }
  ),
  singleColumnMode: generateBooleanSchema(
    "Single Column Mode",
    "When enabled, show only the Runs column in the batting table and hide the bowling and fall-of-wickets sections.",
    { defaultValue: false }
  ),

  // ── Cricket-only: bowling-specific section header overrides ──
  // The mock paints the bowling band light grey with dark text, while batting is blue/white.
  sectionHeaderBowlingContainer: applyAnnotationsToSchema(
    generateContainerSchema(
      "Section Header Band (Bowling)",
      "Container styling for the bowling section header band."
    ),
    { background: { visible: true, color: NEUTRAL_BAND }, padding: '0.75rem' }
  ),
  txtSectionHeaderBowling: TextParticleSpec.generateSchema(
    "Section Header Text (Bowling)",
    "Typography for the bowling section header band title.",
    {
      defaultValues: { size: '1.25rem', weight: '700', wrap: 'nowrap', color: TEXT_DARK_GREY },
      uneditableFields: { text: true },
    }
  ),
  txtColumnHeaderBowling: TextParticleSpec.generateSchema(
    "Column Header Text (Bowling)",
    "Typography for column headers within the bowling section.",
    {
      defaultValues: { size: '1rem', weight: '700', align: 'right', color: TEXT_DARK_GREY },
      uneditableFields: { text: true },
    }
  ),

  // ── Cricket-only: fall of wickets footer ──
  paddingFallOfWickets: generateStringSchema(
    "Fall of Wickets Padding",
    "Padding inside the fall-of-wickets footer.",
    { defaultValue: '0 0.75rem' }
  ),
  txtFallOfWickets: TextParticleSpec.generateSchema(
    "Fall of Wickets Text",
    "Typography for the fall-of-wickets line.",
    {
      defaultValues: {
        size: '0.875rem',
        weight: '400',
        wrap: 'wrap',
        color: TEXT_DARK_GREY,
        lineHeight: '1.3125',
      },
      uneditableFields: { text: true },
    }
  ),
}));
